import React, { useEffect, useMemo, useState } from 'react';
import { API_URLS } from '../../../config/api';
import { Check, Shield, Pencil, Trash2, Plus, ToggleLeft, ToggleRight, Star, X, CheckCircle } from 'lucide-react';
import { Switch } from '../../ui/switch';

const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true';

const AdminSSL = () => {
  const [loading, setLoading] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editingTab, setEditingTab] = useState(null);
  const [editingPkg, setEditingPkg] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        fetch(API_URLS.ADMIN.SSL_TABS),
        fetch(API_URLS.ADMIN.SSL_PACKAGES)
      ]);
      const tJson = await tRes.json();
      const pJson = await pRes.json();
      setTabs(Array.isArray(tJson.data) ? tJson.data : []);
      
      // Parse packages - features obje formatına çevir
      const parsedPackages = (Array.isArray(pJson.data) ? pJson.data : []).map(pkg => {
        let rawFeatures = [];
        if (typeof pkg.features === 'string') {
          try { rawFeatures = JSON.parse(pkg.features); } catch { rawFeatures = []; }
        } else if (Array.isArray(pkg.features)) {
          rawFeatures = pkg.features;
        }
        
        const parsedFeatures = rawFeatures.map(f => {
          if (typeof f === 'string') {
            return { name: f, included: true };
          }
          if (f && typeof f === 'object') {
            return { name: f.name || f.text || '', included: f.included !== false };
          }
          return { name: String(f), included: true };
        });
        
        return { ...pkg, features: parsedFeatures };
      });
      
      setPackages(parsedPackages);
    } catch (e) {
      console.error('SSL fetch error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const emptyTab = { id: null, key: '', label: '', is_active: true, sort_order: 0 };
  const emptyPkg = { id: null, name: '', type: 'dv', warranty: '', features: [], price: '', period: '/yıl', is_active: true, is_best: false, sort_order: 0 };

  const handleTabSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      key: editingTab.key,
      label: editingTab.label,
      is_active: toBool(editingTab.is_active) ? 1 : 0,
      sort_order: Number(editingTab.sort_order) || 0
    };
    try {
      if (editingTab.id) {
        await fetch(API_URLS.ADMIN.UPDATE_SSL_TAB(editingTab.id), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      } else {
        await fetch(API_URLS.ADMIN.CREATE_SSL_TAB, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      }
      setEditingTab(null);
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const handlePkgSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: editingPkg.name,
      type: editingPkg.type,
      warranty: editingPkg.warranty,
      features: Array.isArray(editingPkg.features) ? JSON.stringify(editingPkg.features) : (editingPkg.features || '[]'),
      price: editingPkg.price,
      period: editingPkg.period,
      is_active: toBool(editingPkg.is_active) ? 1 : 0,
      is_best: toBool(editingPkg.is_best) ? 1 : 0,
      sort_order: Number(editingPkg.sort_order) || 0
    };
    try {
      if (editingPkg.id) {
        await fetch(API_URLS.ADMIN.UPDATE_SSL_PACKAGE(editingPkg.id), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      } else {
        await fetch(API_URLS.ADMIN.CREATE_SSL_PACKAGE, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      }
      setEditingPkg(null);
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const toggleTab = async (id, current) => {
    await fetch(API_URLS.ADMIN.TOGGLE_SSL_TAB_STATUS(id), { method: 'PATCH' });
    fetchAll();
  };
  const deleteTab = async (id) => { await fetch(API_URLS.ADMIN.DELETE_SSL_TAB(id), { method: 'DELETE' }); fetchAll(); };

  const togglePkg = async (id) => { await fetch(API_URLS.ADMIN.TOGGLE_SSL_PACKAGE_STATUS(id), { method: 'PATCH' }); fetchAll(); };
  const toggleBest = async (id) => { await fetch(API_URLS.ADMIN.TOGGLE_SSL_PACKAGE_BEST(id), { method: 'PATCH' }); fetchAll(); };
  const deletePkg = async (id) => { await fetch(API_URLS.ADMIN.DELETE_SSL_PACKAGE(id), { method: 'DELETE' }); fetchAll(); };

  const addFeature = () => {
    setEditingPkg(prev => ({
      ...prev,
      features: [...(prev.features || []), { name: '', included: true }]
    }));
  };

  const removeFeature = (index) => {
    setEditingPkg(prev => ({
      ...prev,
      features: (prev.features || []).filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index, field, value) => {
    setEditingPkg(prev => ({
      ...prev,
      features: (prev.features || []).map((feature, i) => 
        i === index ? { ...feature, [field]: value } : feature
      )
    }));
  };

  const toggleFeatureIncluded = (idx) => {
    setEditingPkg(prev => {
      const updatedFeatures = [...(prev.features || [])];
      if (updatedFeatures[idx]) {
        updatedFeatures[idx] = {
          ...updatedFeatures[idx],
          included: !updatedFeatures[idx].included
        };
      }
      return { ...prev, features: updatedFeatures };
    });
  };

  const parseFeatures = (v) => {
    let rawFeatures = [];
    if (typeof v === 'string') {
      try { rawFeatures = JSON.parse(v); } catch { rawFeatures = []; }
    } else if (Array.isArray(v)) {
      rawFeatures = v;
    }
    
    return rawFeatures.map(f => {
      if (typeof f === 'string') {
        return { name: f, included: true };
      }
      if (f && typeof f === 'object') {
        return { name: f.name || f.text || '', included: f.included !== false };
      }
      return { name: String(f), included: true };
    });
  };

  return (
    <div className="space-y-6 sm:space-y-10 p-4 sm:p-6 overflow-x-hidden">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <h2 className="text-lg sm:text-xl font-semibold break-words">SSL Yönetimi</h2>
        {loading && <span className="text-xs sm:text-sm text-gray-500">Yükleniyor...</span>}
      </div>

      {/* Tabs Manager */}
      <section className="border rounded-xl p-4 sm:p-5 bg-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <h3 className="font-semibold text-base sm:text-lg">Sekmeler</h3>
          <button onClick={() => setEditingTab({ ...emptyTab })} className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-sm w-full sm:w-auto"><Plus className="w-4 h-4" /> Yeni Sekme</button>
        </div>
        
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Key</th>
                <th className="py-2 pr-4">Etiket</th>
                <th className="py-2 pr-4">Durum</th>
                <th className="py-2 pr-4">Sıra</th>
                <th className="py-2 pr-4">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {tabs.map(t => (
                <tr key={t.id} className="border-t">
                  <td className="py-2 pr-4 font-mono break-words">{t.key}</td>
                  <td className="py-2 pr-4 break-words">{t.label}</td>
                  <td className="py-2 pr-4">
                    <button onClick={() => toggleTab(t.id, t.is_active)} className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded-md border">
                      {toBool(t.is_active) ? <><ToggleRight className="w-4 h-4 text-green-600" /> Aktif</> : <><ToggleLeft className="w-4 h-4 text-gray-400" /> Pasif</>}
                    </button>
                  </td>
                  <td className="py-2 pr-4">{t.sort_order}</td>
                  <td className="py-2 pr-4 flex items-center gap-2">
                    <button onClick={() => setEditingTab({ ...t })} className="h-8 w-8 inline-flex items-center justify-center rounded-md border hover:bg-gray-50"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => deleteTab(t.id)} className="h-8 w-8 inline-flex items-center justify-center rounded-md border hover:bg-gray-50"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {tabs.map(t => (
            <div key={t.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm font-semibold break-words mb-1">{t.key}</div>
                  <div className="text-sm text-gray-700 break-words">{t.label}</div>
                </div>
                <button onClick={() => toggleTab(t.id, t.is_active)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border flex-shrink-0 ml-2">
                  {toBool(t.is_active) ? <><ToggleRight className="w-3 h-3 text-green-600" /> Aktif</> : <><ToggleLeft className="w-3 h-3 text-gray-400" /> Pasif</>}
                </button>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-xs text-gray-500">Sıra: {t.sort_order}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditingTab({ ...t })} className="h-8 w-8 inline-flex items-center justify-center rounded-md border hover:bg-gray-50"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deleteTab(t.id)} className="h-8 w-8 inline-flex items-center justify-center rounded-md border hover:bg-gray-50"><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {editingTab && (
          <form onSubmit={handleTabSubmit} className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3">
            <input value={editingTab.key} onChange={e => setEditingTab({ ...editingTab, key: e.target.value })} placeholder="key (dv/ov/ev/wildcard/deals)" className="border rounded-md h-10 px-3 w-full text-sm" />
            <input value={editingTab.label} onChange={e => setEditingTab({ ...editingTab, label: e.target.value })} placeholder="Etiket" className="border rounded-md h-10 px-3 w-full text-sm" />
            <input value={editingTab.sort_order} onChange={e => setEditingTab({ ...editingTab, sort_order: e.target.value })} placeholder="Sıra" className="border rounded-md h-10 px-3 w-full text-sm" />
            <select value={toBool(editingTab.is_active) ? '1' : '0'} onChange={e => setEditingTab({ ...editingTab, is_active: e.target.value === '1' })} className="border rounded-md h-10 px-3 w-full text-sm">
              <option value="1">Aktif</option>
              <option value="0">Pasif</option>
            </select>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <button type="submit" className="h-10 px-4 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-sm w-full sm:w-auto">Kaydet</button>
              <button type="button" onClick={() => setEditingTab(null)} className="h-10 px-4 rounded-md border text-sm w-full sm:w-auto">Vazgeç</button>
            </div>
          </form>
        )}
      </section>

      {/* Packages Manager */}
      <section className="border rounded-xl p-4 sm:p-5 bg-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <h3 className="font-semibold text-base sm:text-lg">SSL Paketleri</h3>
          <button onClick={() => setEditingPkg({ ...emptyPkg })} className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-sm w-full sm:w-auto"><Plus className="w-4 h-4" /> Yeni Paket</button>
        </div>
        
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Ad</th>
                <th className="py-2 pr-4">Tür</th>
                <th className="py-2 pr-4">Garanti</th>
                <th className="py-2 pr-4">Fiyat</th>
                <th className="py-2 pr-4">Dönem</th>
                <th className="py-2 pr-4">Özellikler</th>
                <th className="py-2 pr-4">Durum</th>
                <th className="py-2 pr-4">Önerilen</th>
                <th className="py-2 pr-4">Sıra</th>
                <th className="py-2 pr-4">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {packages.map(p => (
                <tr key={p.id} className="border-t align-top">
                  <td className="py-2 pr-4 font-medium break-words">{p.name}</td>
                  <td className="py-2 pr-4 uppercase text-xs break-words">{p.type}</td>
                  <td className="py-2 pr-4 break-words">{p.warranty}</td>
                  <td className="py-2 pr-4 break-words">{p.price}</td>
                  <td className="py-2 pr-4 break-words">{p.period}</td>
                  <td className="py-2 pr-4">
                    <ul className="list-disc ml-4 text-gray-600">
                      {parseFeatures(p.features).slice(0, 2).map((f, i) => {
                        const featureName = f.name || f.text || f;
                        const isIncluded = typeof f === 'object' ? (f.included !== false) : true;
                        return (
                          <li key={i} className={`break-words ${isIncluded ? '' : 'text-gray-400 line-through'}`}>
                            {featureName}
                          </li>
                        );
                      })}
                      {parseFeatures(p.features).length > 2 && <li className="text-gray-400">... ({parseFeatures(p.features).length - 2} daha)</li>}
                    </ul>
                  </td>
                  <td className="py-2 pr-4">
                    <button onClick={() => togglePkg(p.id)} className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded-md border">
                      {toBool(p.is_active) ? 'Aktif' : 'Pasif'}
                    </button>
                  </td>
                  <td className="py-2 pr-4">
                    <button onClick={() => toggleBest(p.id)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border">
                      <Star className={`w-4 h-4 ${toBool(p.is_best) ? 'text-yellow-500' : 'text-gray-400'}`} />
                      {toBool(p.is_best) ? 'Önerilen' : '—'}
                    </button>
                  </td>
                  <td className="py-2 pr-4">{p.sort_order}</td>
                  <td className="py-2 pr-4 flex items-center gap-2">
                    <button onClick={() => setEditingPkg({ ...p, features: parseFeatures(p.features) })} className="h-8 w-8 inline-flex items-center justify-center rounded-md border hover:bg-gray-50"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => deletePkg(p.id)} className="h-8 w-8 inline-flex items-center justify-center rounded-md border hover:bg-gray-50"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {packages.map(p => (
            <div key={p.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-base break-words mb-1">{p.name}</div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="uppercase text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded break-words">{p.type}</span>
                    {toBool(p.is_best) && (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded flex items-center gap-1">
                        <Star className="w-3 h-3" /> Önerilen
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => togglePkg(p.id)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border flex-shrink-0 ml-2">
                  {toBool(p.is_active) ? 'Aktif' : 'Pasif'}
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                <div>
                  <span className="text-gray-600 text-xs">Garanti:</span>
                  <div className="font-medium break-words">{p.warranty || '-'}</div>
                </div>
                <div>
                  <span className="text-gray-600 text-xs">Fiyat:</span>
                  <div className="font-medium break-words">{p.price} {p.period}</div>
                </div>
              </div>

              {parseFeatures(p.features).length > 0 && (
                <div className="mb-3">
                  <span className="text-gray-600 text-xs mb-1 block">Özellikler:</span>
                  <ul className="list-disc ml-4 text-sm text-gray-600">
                    {parseFeatures(p.features).slice(0, 3).map((f, i) => {
                      const featureName = f.name || f.text || f;
                      const isIncluded = typeof f === 'object' ? (f.included !== false) : true;
                      return (
                        <li key={i} className={`break-words ${isIncluded ? '' : 'text-gray-400 line-through'}`}>
                          {featureName}
                        </li>
                      );
                    })}
                    {parseFeatures(p.features).length > 3 && <li className="text-gray-400">... ({parseFeatures(p.features).length - 3} daha)</li>}
                  </ul>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-xs text-gray-500">Sıra: {p.sort_order}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditingPkg({ ...p, features: parseFeatures(p.features) })} className="h-8 w-8 inline-flex items-center justify-center rounded-md border hover:bg-gray-50"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deletePkg(p.id)} className="h-8 w-8 inline-flex items-center justify-center rounded-md border hover:bg-gray-50"><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {editingPkg && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="absolute inset-0 bg-black/40" onClick={() => setEditingPkg(null)} />
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-[95vw] sm:max-w-md md:max-w-3xl max-h-[90vh] overflow-y-auto my-auto">
              <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b">
                <h4 className="font-semibold text-base sm:text-lg break-words">SSL Paketi {editingPkg.id ? 'Düzenle' : 'Oluştur'}</h4>
                <button onClick={() => setEditingPkg(null)} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-gray-100 flex-shrink-0"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-4 sm:p-5">
                <form onSubmit={handlePkgSubmit} className="grid grid-cols-1 gap-3">
                  <input value={editingPkg.name} onChange={e => setEditingPkg({ ...editingPkg, name: e.target.value })} placeholder="Paket adı" className="border rounded-md h-10 px-3 w-full text-sm" />
                  <select value={editingPkg.type} onChange={e => setEditingPkg({ ...editingPkg, type: e.target.value })} className="border rounded-md h-10 px-3 w-full text-sm">
                    <option value="dv">DV</option>
                    <option value="ov">OV</option>
                    <option value="ev">EV</option>
                    <option value="wildcard">Wildcard</option>
                    <option value="deals">Fırsat</option>
                  </select>
                  <input value={editingPkg.warranty} onChange={e => setEditingPkg({ ...editingPkg, warranty: e.target.value })} placeholder="Garanti ($)" className="border rounded-md h-10 px-3 w-full text-sm" />
                  <input value={editingPkg.price} onChange={e => setEditingPkg({ ...editingPkg, price: e.target.value })} placeholder="Fiyat" className="border rounded-md h-10 px-3 w-full text-sm" />
                  <input value={editingPkg.period} onChange={e => setEditingPkg({ ...editingPkg, period: e.target.value })} placeholder="/yıl" className="border rounded-md h-10 px-3 w-full text-sm" />
                  
                  {/* Özellikler */}
                  <div className="border rounded-md p-3 sm:p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Özellikler</label>
                    <div className="space-y-2">
                      {(editingPkg.features || []).map((feature, index) => {
                        const featureName = typeof feature === 'string' 
                          ? feature 
                          : (feature?.name || '');
                        const isIncluded = typeof feature === 'object' ? (feature.included !== false) : true;
                        return (
                          <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 border rounded px-3 py-2 bg-gray-50">
                            <div className="flex items-center gap-2 sm:gap-3 flex-1 w-full sm:w-auto min-w-0">
                              <Switch 
                                checked={isIncluded}
                                onCheckedChange={() => toggleFeatureIncluded(index)}
                                className="data-[state=checked]:bg-green-600 flex-shrink-0"
                              />
                              <input
                                value={featureName}
                                onChange={(e) => updateFeature(index, 'name', e.target.value)}
                                placeholder="Özellik adı"
                                className={`flex-1 min-w-0 border-0 bg-transparent focus:outline-none text-sm ${isIncluded ? '' : 'text-gray-400'}`}
                              />
                              <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0 ${isIncluded ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                {isIncluded ? 'Dahil' : 'Hariç'}
                              </span>
                            </div>
                            <button type="button" onClick={() => removeFeature(index)} className="text-red-600 hover:text-red-700 flex-shrink-0 self-end sm:self-auto">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={addFeature}
                        className="w-full border rounded-md h-10 px-3 text-sm text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-2"
                      >
                        <Plus className="w-4 h-4" /> Özellik Ekle
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={toBool(editingPkg.is_active)} onChange={e => setEditingPkg({ ...editingPkg, is_active: e.target.checked })} className="flex-shrink-0" /> Aktif</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={toBool(editingPkg.is_best)} onChange={e => setEditingPkg({ ...editingPkg, is_best: e.target.checked })} className="flex-shrink-0" /> Önerilen</label>
                    <input value={editingPkg.sort_order} onChange={e => setEditingPkg({ ...editingPkg, sort_order: e.target.value })} placeholder="Sıra" className="border rounded-md h-10 px-3 w-full text-sm" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <button type="submit" className="h-10 px-4 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-sm w-full">Kaydet</button>
                    <button type="button" onClick={() => setEditingPkg(null)} className="h-10 px-4 rounded-md border text-sm w-full">Vazgeç</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminSSL;





